import {DateRange} from '@blueprintjs/datetime2';
import * as geojson from 'geojson';
import * as I from 'immutable';

import {NoteGraph} from 'app/components/AnalyzePolygonChart/types';
import {
  ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY,
  ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY,
  APP_PROPERTIES_KEY,
  APP_PROPERTY_ASSIGNEE_EMAIL,
  APP_PROPERTY_ASSIGNEE_EMAILS,
  APP_PROPERTY_REPORTING_DUE_DATE,
  APP_PROPERTY_STREAMIMAGERY_API_KEY,
  APP_PROPERTY_WMTS_URL,
  CUSTOM_ALERT_TYPE_CATEGORY,
  CUSTOM_ALERT_TYPE_COMPOSITE,
  CUSTOM_ALERT_TYPE_THRESHOLD,
  CUSTOM_ALERT_TYPE_VEGETATION_DROP,
  FEATURE_PROCESSING_COMPLETE_STATUS,
  FEATURE_WAITING_FOR_PROCESSING_STATUS,
  LEGACY_ALERT_VEGETATION_DROP_ENROLLMENT_KEY,
  NOTE_TYPE_AGENT,
  NOTE_TYPE_ALERT,
  NOTE_TYPE_SYSTEM_NEW_IMAGERY_AVAILABLE,
  NOTE_TYPE_SYSTEM_NEW_IMAGERY_PROCESSED,
  NOTE_TYPE_USER,
  RDM_PROPERTIES_COMPLIANCE_KEY,
  RDM_PROPERTIES_KEY,
} from 'app/utils/constants';
import {BBox2d} from 'app/utils/geoJsonUtils';
import {MapImageRef} from 'app/utils/mapUtils';
import {TagStatusMap} from 'app/utils/tagUtils';

import {ApiOrganizationUser} from '../Organization';

export interface ApiFeature extends geojson.Feature<geojson.Geometry, ApiFeatureCommonProperties> {
  id: number;
}

/**
 * Properties is allowed to have more keys / values, but there's not a good
 * way to type those with Immutable and also preserve the known types of the
 * common properties. Cast this to any as necessary.
 */
export interface ApiFeatureCommonProperties {
  id: number;
  featureCollectionId: number;

  lensId?: string;

  name: string;
  /** An optional name for a feature in a multi-feature Lens property. By default, we order the
   * features by their ID and assign a unique letter in alphabetical order. */
  multiFeaturePartName?: string;

  /** an optional property to reference an external url resource related to the
   * feature. For now, this allows us to link user-point vectors in Lens with
   * corresponding timeseries in Grafana. */
  url?: string;

  isArchived: boolean;

  validatedGeometry?: geojson.Geometry;
  originalPolygon?: geojson.Polygon;

  /** ISO8601 date */
  createdAt: string;
  /** ISO8601 date */
  updatedAt: string;

  status: FeatureStatus;

  [APP_PROPERTIES_KEY]?: {
    /** Kept in sync with the first entry of `assigneeEmails` for readers that predate it. */
    [APP_PROPERTY_ASSIGNEE_EMAIL]?: string;
    /**
     * Who this property is assigned to. A map for the same reason `tagIds` is one: it lets a
     * bulk edit add or remove one person without sending the rest of the set. A removal is
     * recorded as `false`, not by deleting the key.
     */
    [APP_PROPERTY_ASSIGNEE_EMAILS]?: Record<string, boolean>;
    /** Date in the YYYY-MM-DD format */
    [APP_PROPERTY_REPORTING_DUE_DATE]?: string;
    /** First element is the source ID, second is the cursor (+00:00 timezone) */
    hiddenScenes?: [string, string][];
    /**
     * Ids of labels for this property. See TagSetting in the LENS
     * FeatureCollection view. Stored as a map so we can use merging on the
     * server to add / remove a tag from a bunch of features in bulk w/o messing
     * with other tags.
     */
    tagIds?: Record<string, boolean>;
    [LEGACY_ALERT_VEGETATION_DROP_ENROLLMENT_KEY]?: boolean;
    [ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY]?: boolean;
    [ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY]?: boolean;

    [APP_PROPERTY_WMTS_URL]?: string;
    [APP_PROPERTY_STREAMIMAGERY_API_KEY]?: string;
  };

  [RDM_PROPERTIES_KEY]?: {
    [RDM_PROPERTIES_COMPLIANCE_KEY]?: [{year: number; compliance: boolean}];
  };

  __system?: {
    paidImagery?: SystemPaidImagery;
  };
}

export type FeatureStatus =
  | typeof FEATURE_PROCESSING_COMPLETE_STATUS
  | typeof FEATURE_WAITING_FOR_PROCESSING_STATUS
  | null;

export type AlertEnrollmentKeys =
  | typeof LEGACY_ALERT_VEGETATION_DROP_ENROLLMENT_KEY
  | typeof ALERT_OWNERSHIP_CHANGE_ENROLLMENT_KEY
  | typeof ALERT_PARCEL_BOUNDARY_CHANGE_ENROLLMENT_KEY;

export type AlertTypeProperties =
  | ThresholdAlertProperties
  | CategoryAlertProperties
  | VegetationDropAlertProperties
  | CompositeAlertProperties;

export interface OverlayFilter {
  // The attribute name is a value, never an object key, so the keys-only snake/camel conversion
  // on the way to and from the API leaves it alone.
  attribute: string;
  values: string[];
}

export interface OverlaySelection {
  featureCollectionId: number;
  filter: OverlayFilter | null;
}

export type OverlayListKey = 'includeOverlays' | 'excludeOverlays';

export interface BaseAlertPropertyConfiguration {
  // The one overlay a policy could have before the lists. The API still carries it beside them;
  // normalizeOverlaySelections merges it into its list on the way in and clears it, so the app
  // only ever writes overlays through the lists.
  overlayFeatureCollectionId: number | null;
  overlayMethod: 'intersection' | 'difference' | null;
  // Monitor only inside any of these; none means the whole property.
  includeOverlays: OverlaySelection[];
  // Never monitor inside any of these. Applied after the includes.
  excludeOverlays: OverlaySelection[];
  minimumAlertingArea: number;
  dateRange: DateRange | undefined;
}
export interface ThresholdAlertProperties extends BaseAlertPropertyConfiguration {
  value: number;
  direction: AlertThresholdDirection;
  percentOfProperty: number;
  numConsecutiveScenes: number;
}

export interface CategoryAlertProperties extends BaseAlertPropertyConfiguration {
  fromCategories: string[];
  toCategories: string[];
}

export interface VegetationDropAlertProperties extends BaseAlertPropertyConfiguration {}

// An unset bound is the one-sided case, so a range condition is a superset of a threshold's
// direction/value pair.
export interface RangeCondition {
  // Client-only: lets a row track its own draft state across reorders. Stripped before the
  // condition is sent to the API, which has no notion of it.
  id: string;
  layerId: string;
  min: number | null;
  max: number | null;
  // A threshold lookout compares strictly, so a policy converted from one says so here. Only the
  // API sets these; the builder always writes inclusive bounds.
  minInclusive?: boolean;
  maxInclusive?: boolean;
}

export interface CompositeAlertProperties extends BaseAlertPropertyConfiguration {
  // OR of ANDs: a pixel alerts when it satisfies every condition in any one group.
  groups: RangeCondition[][];
  // The combined mask is gated exactly as a threshold's own mask is.
  percentOfProperty: number;
  numConsecutiveScenes: number;
}

export type APIAlertPolicyProperties =
  | APIThresholdAlertPropertyConfiguration
  | APICategoryAlertPropertyConfiguration
  | APIVegetationDropAlertPropertyConfiguration
  | APICompositeAlertPropertyConfiguration;

export interface APIOverlayFilter {
  attribute: string;
  values: string[];
}

export interface APIOverlaySelection {
  feature_collection_id: number;
  filter: APIOverlayFilter | null;
}

export interface APIBaseAlertPropertyConfiguration {
  overlay_feature_collection_id: number | null;
  overlay_method: 'intersection' | 'difference' | null;
  include_overlays: APIOverlaySelection[];
  exclude_overlays: APIOverlaySelection[];
  minimum_alerting_area: number | null;
  date_range: ApiAlertDateRange;
}

export type ApiAlertDateRange = [{month: number; day: number}, {month: number; day: number}] | null;

export interface APIThresholdAlertPropertyConfiguration extends APIBaseAlertPropertyConfiguration {
  direction: AlertThresholdDirection;
  value: number;
  percent_of_property: number;
  num_consecutive_scenes: number;
}

export interface APICategoryAlertPropertyConfiguration extends APIBaseAlertPropertyConfiguration {
  from_categories: string[];
  to_categories: string[];
}

export interface APIVegetationDropAlertPropertyConfiguration extends APIBaseAlertPropertyConfiguration {}

export interface APIRangeCondition {
  layer_id: string;
  min: number | null;
  max: number | null;
  min_inclusive?: boolean;
  max_inclusive?: boolean;
}

export interface APICompositeAlertPropertyConfiguration extends APIBaseAlertPropertyConfiguration {
  groups: APIRangeCondition[][];
  percent_of_property: number;
  num_consecutive_scenes: number;
}

export enum AlertThresholdDirection {
  Above = 'above',
  Below = 'below',
}

export type AlertPolicy = {
  id: number;
  featureCollectionId: string | number;
  sourceId: string;
  layerId: string;
  name: string;
  description: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  isArchived: boolean;
  enrollments: ApiAlertEnrollment[];
} & (
  | {alertType: typeof CUSTOM_ALERT_TYPE_THRESHOLD; properties: ThresholdAlertProperties}
  | {alertType: typeof CUSTOM_ALERT_TYPE_CATEGORY; properties: CategoryAlertProperties}
  | {alertType: typeof CUSTOM_ALERT_TYPE_VEGETATION_DROP; properties: VegetationDropAlertProperties}
  | {alertType: typeof CUSTOM_ALERT_TYPE_COMPOSITE; properties: CompositeAlertProperties}
);

export type ApiAlertPolicy = Omit<AlertPolicy, 'enrollments'>;

export interface ApiAlertEnrollment {
  id: number;
  alertPolicyId: number;
  featureId: number;
  geometry: geojson.Geometry;
  status: 'inactive' | 'active';
}

export interface SystemPaidImagery {
  limit: number;
  /**
   * First element is the source ID, second is the sensing time (Z timezone,
   * which is _not_ the same as cursors, which use +00:00 timezone)
   */
  scenes: [string, string][];
}

export interface RasterSourceDefinition {
  bounds: [number, number, number, number];
  format: 'png';
  maxzoom: number;
  minzoom: number;
  tilejson: string;
  tiles: string[];
}

const OPTIONAL_FEATURE_MEASUREMENTS = [
  'copyright',
  'sensor_name',
  'vendor_name',
  'cloud_cover_percentage',
];
export type OptionalFeatureMeasurement = (typeof OPTIONAL_FEATURE_MEASUREMENTS)[number];
export function isOptionalFeatureMeasurement(value: string): boolean {
  return OPTIONAL_FEATURE_MEASUREMENTS.includes(value);
}

export type FeatureDatumBounds = BBox2d | geojson.Polygon | geojson.MultiPolygon;
export interface ApiFeatureData {
  images: {
    bounds: FeatureDatumBounds;
    /** ISO8601 date */
    date: string;
    featureId: number;
    templateUrls: Record<string, RasterSourceDefinition>;
    urls: Record<string, string>;
    cogUrls?: Record<
      string,
      Record<
        string,
        {
          url: string;
          urls: string;
        }
      >
    >;
  };

  isFullyOrdered: boolean;

  /** ISO8601 */
  date: string;

  measurements: {
    [key: string]: null | number | string | FeatureDatumBounds | any;
    //the following values are stored in the OPTIONAL_FEATURE_MEASUREMENTS type above
    copyright?: string;
    sensor_name?: string;
    vendor_name?: string;
    cloud_cover_percentage?: number;
  };

  types: string[];
  sources: string[];
}

export interface ApiNote {
  // Notes have numeric IDs in the database, but "pending" notes have string IDs
  id: number | string;
  // User notes have a sequential number (separate from the id) that's displayed
  displayNumber: number | null;

  projectId: string;
  featureCollectionId: number;
  featureId: number;
  organizationId: string;
  /** Email address of the author */
  userId: string;
  userName?: string;

  /** ISO8601 string */
  createdAt: string;
  /** ISO8601 string */
  updatedAt: string;

  geometry: null | geojson.Geometry;
  layerKey: null | string;

  isArchived: boolean;

  text: string;
  attachments: null | string[];

  cursorKey: null | string;
  cursorType: null | 'datetime' | 'compareDatetime';

  noteType:
    | typeof NOTE_TYPE_USER
    | typeof NOTE_TYPE_ALERT
    | typeof NOTE_TYPE_SYSTEM_NEW_IMAGERY_AVAILABLE
    | typeof NOTE_TYPE_SYSTEM_NEW_IMAGERY_PROCESSED
    | typeof NOTE_TYPE_AGENT;

  graph: NoteGraph | null;

  tagIds: TagStatusMap | null;

  overlayFeatureCollectionIds: number[] | null;

  properties?: {
    alert_policy_id?: number;
    alert_policy_enrollment_id?: number;
    workflow_id?: string;
    run_id?: string;
    agent_run_id?: number;
    // Set by the SAM agents on the single merged note they write once a run passes
    // AGENT_OBSERVATION_LIMIT; observation_count is how many were found before merging.
    observation_limit_reached?: boolean;
    observation_count?: number;
  };
}

export interface ApiReport<T extends ReportState | string> {
  id: number;
  featureId: ApiFeature['id'];
  userId: ApiOrganizationUser['id'];
  title: string;
  createdAt: string;
  // firebase user id
  createdBy: string;
  createdByUser: ReportUser;
  updatedAt: string;
  // firebase user id
  updatedBy: string;
  updatedByUser: ReportUser;
  isArchived: boolean;
  isMultilocation: boolean;
  data: T;
}

export interface ReportUser {
  id: string;
  email: string;
  name: string;
}

// A report as listed across a whole portfolio, where the feature it belongs to
// isn't already known from the URL.
export interface ApiProjectReport extends ApiReport<string> {
  feature: {
    id: number;
    lensId: string;
    displayName: string;
  };
}

// The background job that renders the requested reports to PDF and emails them.
export interface ApiReportExportRequest {
  id: number;
  status: string;
}

export interface ApiReportBatchUpdateResponse {
  updated: number;
}

export interface ReportState {
  coverPage: ReportCoverPage;
  overviewPages: ReportOverviewPage[];
  notePages: ReportNotePage[];
}

export interface ReportCoverPage {
  title: string;
  subtitle: string;
  company: string;
  date: string;
  description: string;
  overflowPages?: string[];
}

export interface ReportOverviewPage {
  hidden: boolean;
  title: string;
  id: string;
  currentPropertyPartIndex: number;
  showNotePoints: boolean;
  showMapScale?: boolean;
  //we used to track cursor on the overview page from legacy report days.
  //now we track imageRefs. any new report overview page will have an imageRef,
  //but we can't guarantee the presence of this field in old saved reports.
  cursor?: string;
  imageRef?: MapImageRef;
  overlayVisibilityByName?: Record<string, boolean>;
}

export interface ReportNotePage {
  hidden: boolean;
  title: string;
  chartHidden: boolean;
  attachmentsHidden: boolean;
  id: string | number;
  legendsHidden: boolean;
  currentPropertyPartIndex: number;
  imageAlignment?: 'horizontal' | 'vertical';
  overlayVisibilityByName?: Record<string, boolean>;
  mapBounds?: geojson.BBox;
  fullPageImageLayout?: boolean;
  showMapScale?: boolean;
}

export interface ApiLensImageryRecord {
  date: string;
  feature_id: number;
  source_id: string;
}

export interface ApiOrderableScene {
  thumbnailBbox: BBox2d | geojson.Polygon | geojson.MultiPolygon;
  canBeOrdered: boolean;
  cloudCoverPercentage: number;
  bands?: string[];
  isOrdered: boolean;
  isFullyOrdered: boolean;
  orderedGeometry: null | geojson.Geometry;
  orderProcessed: boolean;
  sceneGeometry: geojson.Geometry;
  sensingTime: string;
  sourceId: string;
  urls: Record<string, string>;
  sensorName: string | null;
}

export type State = I.MapAsRecord<{
  dataByFeatureId: I.Map<number, I.ImmutableOf<ApiFeatureData[]>>;
}>;

export interface ApiBulkOrderVerificationSuccess {
  featureId: number;
  costInCents: number;
  sceneCount: number;
  partial: boolean;
  belowMinimum: boolean;
  error: never;
}

export interface ApiBulkOrderVerificationError {
  featureId: number;
  error: string;
}

export type ApiBulkOrderVerification =
  | ApiBulkOrderVerificationSuccess
  | ApiBulkOrderVerificationError;

export type ApiBulkOrderVerificationResponse = ApiBulkOrderVerification[];

export interface ApiBulkOrderResponse {
  costInCents: number;
}
